import type { ReactNode } from 'react';
import { Button, Drawer, IconButton, Notice, TextField } from '@agency/ui';
import { ApiClientError } from '../../lib/api.ts';

export const errText = (e: unknown) => (e instanceof ApiClientError ? `${e.message}${e.requestId ? ` (Ref ${e.requestId})` : ''}` : 'Something went wrong. Try again.');

/** Field errors keyed by dotted path (`socialLinks.0.url`), the same shape for Zod issues and API field errors. */
export type Errors = Record<string, string>;
export const issueMap = (issues: readonly { path: readonly PropertyKey[]; message: string }[]): Errors => {
  const out: Errors = {};
  for (const i of issues) { const k = i.path.map(String).join('.'); if (!(k in out)) out[k] = i.message; }
  return out;
};
export const serverErrors = (e: unknown): Errors => (e instanceof ApiClientError ? Object.fromEntries(e.fieldErrors.map((f) => [f.field, f.message])) : {});
/** Empty optional inputs are sent as absent, never as empty strings. */
export const orUndef = (s: string) => { const t = s.trim(); return t === '' ? undefined : t; };

export function moveItem<T>(arr: T[], i: number, d: -1 | 1): T[] {
  const j = i + d;
  if (j < 0 || j >= arr.length) return arr;
  const n = [...arr];
  [n[i], n[j]] = [n[j]!, n[i]!];
  return n;
}

/** Move up / move down / remove for one repeated row. Labels name the row so screen readers can tell rows apart. */
const nameOf = (noun: string, index: number, context?: string) => `${noun} ${index + 1}${context ? ` in ${context}` : ''}`;
export function RowControls({ noun, context, index, count, min = 0, onMove, onRemove }: { noun: string; context?: string; index: number; count: number; min?: number; onMove: (d: -1 | 1) => void; onRemove: () => void }) {
  const name = nameOf(noun, index, context);
  return (
    <div className="rep__actions">
      <IconButton icon="chevronUp" label={`Move ${name} up`} aria-disabled={index === 0 || undefined} onClick={() => index > 0 && onMove(-1)} />
      <IconButton icon="chevronDown" label={`Move ${name} down`} aria-disabled={index === count - 1 || undefined} onClick={() => index < count - 1 && onMove(1)} />
      <Button size="sm" variant="ghost" aria-disabled={count <= min || undefined} onClick={() => count > min && onRemove()}>Remove {name}</Button>
    </div>
  );
}

export type LinkRow = { label: string; href: string };
/** A reorderable list of label + link pairs: used for navigation children, footer links and legal links. */
export function LinkListEditor<T extends LinkRow>({ legend, noun, context, links, max, readOnly, onChange, errors, errPrefix, hint, extra }: {
  legend: string; noun: string; context?: string; links: T[]; max: number; readOnly: boolean; onChange: (next: T[]) => void; errors: Errors; errPrefix: string; hint?: string;
  /** Optional extra fields per row (e.g. mega-menu description and icon). */
  extra?: (link: T, index: number, update: (next: T) => void) => ReactNode;
}) {
  return (
    <fieldset className="rep-set">
      <legend>{legend}</legend>
      {hint ? <p className="ag-hint" style={{ margin: 0 }}>{hint}</p> : null}
      {links.length === 0 ? <p className="ag-muted" style={{ margin: 0 }}>None yet.</p> : null}
      {links.map((l, i) => (
        <div className="rep" key={i}>
          <TextField label={`Label (${nameOf(noun, i, context)})`} required value={l.label} readOnly={readOnly} error={errors[`${errPrefix}.${i}.label`]} onChange={(e) => onChange(links.map((x, k) => (k === i ? { ...x, label: e.target.value } : x)))} />
          <TextField label={`Link (${nameOf(noun, i, context)})`} required value={l.href} readOnly={readOnly} hint="An https:// link, a /path or a #anchor" error={errors[`${errPrefix}.${i}.href`]} onChange={(e) => onChange(links.map((x, k) => (k === i ? { ...x, href: e.target.value } : x)))} />
          {extra ? extra(l, i, (next) => onChange(links.map((x, k) => (k === i ? next : x)))) : null}
          {!readOnly ? <RowControls noun={noun} context={context} index={i} count={links.length} onMove={(d) => onChange(moveItem(links, i, d))} onRemove={() => onChange(links.filter((_, k) => k !== i))} /> : null}
        </div>
      ))}
      {!readOnly ? <div><Button size="sm" icon="plus" aria-disabled={links.length >= max || undefined} onClick={() => links.length < max && onChange([...links, { label: '', href: '' } as T])}>Add {noun}{context ? <span className="ag-visually-hidden"> to {context}</span> : null}</Button>{links.length >= max ? <span className="ag-hint"> Up to {max}.</span> : null}</div> : null}
    </fieldset>
  );
}

/** Modal confirmation for destructive actions. Cancel is focused first so Enter never deletes by accident. */
export function ConfirmDrawer({ open, title, confirmLabel, pending, error, onConfirm, onClose, children }: {
  open: boolean; title: string; confirmLabel: string; pending: boolean; error?: string | null; onConfirm: () => void; onClose: () => void; children: ReactNode;
}) {
  return (
    <Drawer open={open} onClose={onClose} title={title} width={420}>
      <div className="formgrid">
        {error ? <Notice tone="error" title="Could not complete">{error}</Notice> : null}
        <div>{children}</div>
        <div className="actions__row">
          <Button data-autofocus onClick={onClose}>Cancel</Button>
          <Button variant="danger" loading={pending} onClick={onConfirm}>{confirmLabel}</Button>
        </div>
      </div>
    </Drawer>
  );
}

/** A single labelled checkbox with optional hint, matching the inspector's checkbox layout. */
export function CheckField({ label, hint, checked, disabled, onChange }: { label: string; hint?: string; checked: boolean; disabled?: boolean; onChange: (v: boolean) => void }) {
  return (
    <div className="ag-field">
      <label style={{ display: 'inline-flex', gap: 8, alignItems: 'center' }}>
        <input type="checkbox" checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} /> {label}
      </label>
      {hint ? <span className="ag-hint">{hint}</span> : null}
    </div>
  );
}
